import React, { useEffect, useRef, useState } from 'react';
import { Box, Flex, Link, Text, IconButton, Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon } from "@chakra-ui/react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { HamburgerIcon } from "@chakra-ui/icons";
import { MdAccountCircle } from "react-icons/md";
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { signOut } from '../store/authSlice';
import { persistor } from '../store/store';
import { CEAS_APP_VERSION, UserRole } from '../contexts/constants';
import InquireModal from '../pages/Modal/InquireModal';

/**
 * 共通画面（メニュー画面）
 * @returns
 */
const Menu: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [isInquireOpen, setIsInquireOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const email = useAppSelector((state) => state.auth.email);
  const isMasterMaintenanceAccessible = useAppSelector((state) => state.employee.isMasterMaintenanceAccessible);
  const userRole = useAppSelector((state) => state.employee.userRole);

  // マスタメンテナンス表示可否（システム利用設定の「マスタメンテナンス表示」で制御）
  const canShowMasterMaintenance = isMasterMaintenanceAccessible === true;
  // 事業管理一覧表示可否（マスタメンテナンス表示：true かつ 事業管理権限）
  const canShowManagementList = isMasterMaintenanceAccessible === true && (userRole === UserRole.ManagementAdmin.code || userRole === UserRole.CorporateAdmin.code);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  // サインアウト
  const handleClickSignOut = async () => {
    try {
      await dispatch(signOut());
    } catch (err) {
      console.error('サインアウトに失敗しました。', err);
    } finally {
      // Cognito SDK残存キーの削除（セッション期限切れ時にcleanClientDataがスキップされる問題の対策）
      Object.keys(sessionStorage)
        .filter(key => key.startsWith('CognitoIdentityServiceProvider.'))
        .forEach(key => sessionStorage.removeItem(key));
      // redux-persistデータの削除（email, userId, 従業員データ等）
      await persistor.purge();
      // アプリ固有キーの削除
      sessionStorage.removeItem('projectId');
      navigate('/signin', { replace: true });
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current && !menuRef.current.contains(event.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <>
      <IconButton
        ref={buttonRef}
        icon={<HamburgerIcon />}
        onClick={handleToggle}
        aria-label="Toggle menu"
        size="sm"
        position="fixed"
        top="16px"
        right="16px"
        zIndex="10"
        bg="#0D47A1"
        color="white"
        _hover={{ bg: "#1565C0" }}
      />
      {/* メニュー画面 */}
      {isOpen && (
        <Box
          ref={menuRef}
          position="fixed"
          top={14}
          right={4}
          width="500px"
          height="82vh"
          bg="#0D47A1"
          color="white"
          p={4}
          zIndex="9"
          overflowY="auto"
          borderRadius="16px"
        >
          {/* ユーザー情報 */}
          <Flex align="center" mb={4}>
            <MdAccountCircle style={{ height: "40px", width: "40px" }} />
            <Text py="4px" px="8px" fontWeight="bold" fontSize="lg">{email}</Text>
          </Flex>

          {/* メニュー項目 */}
          <Accordion defaultIndex={[0, 1, 2, 3, 4]} mb="16px" allowMultiple>
            <AccordionItem borderColor="#1976D2">
              <AccordionButton justifyContent='space-between' fontSize="18px" fontWeight="bold" textAlign="left">{t('components.Menu.section.home')}<AccordionIcon /></AccordionButton>
              <AccordionPanel py={2} pl="32px" textAlign="left">
                <Link as={RouterLink} to="/home" display="block" px="8px" py="4px" fontWeight="normal" onClick={handleToggle}>
                  {t('components.Menu.item.homeScreen')}
                </Link>
              </AccordionPanel>
            </AccordionItem>
            <AccordionItem borderColor="#1976D2">
              <AccordionButton justifyContent='space-between' fontSize="18px" fontWeight="bold" textAlign="left">{t('components.Menu.section.investment')}<AccordionIcon /></AccordionButton>
              <AccordionPanel py={2} pl="32px" textAlign="left">
                <Link
                  as={RouterLink}
                  to="/investment-screening-detail"
                  state={{ projectId: "" }}
                  display="block"
                  px="8px"
                  py="4px"
                  fontWeight="normal"
                  onClick={() => {
                    sessionStorage.removeItem("projectId");
                    sessionStorage.removeItem("reviewDate");
                    handleToggle();
                  }}>
                  {t('components.Menu.item.newInvestmentApplication')}
                </Link>
                <Link as={RouterLink} to="/input-list" display="block" px="8px" py="4px" fontWeight="normal" onClick={() => { sessionStorage.removeItem("reviewDate"); handleToggle(); }}>
                  {t('components.Menu.item.inputList')}
                </Link>
                <Link as={RouterLink} to="/approve-list" display="block" px="8px" py="4px" fontWeight="normal" onClick={() => { sessionStorage.removeItem("reviewDate"); handleToggle(); }}>
                  {t('components.Menu.item.approveList')}
                </Link>
                <Link as={RouterLink} to="/investment-list" display="block" px="8px" py="4px" fontWeight="normal" onClick={() => { sessionStorage.removeItem("reviewDate"); handleToggle(); }}>
                  {t('components.Menu.item.investmentList')}
                </Link>
                {canShowManagementList && (
                  <Link as={RouterLink} to="/management-list" display="block" px="8px" py="4px" fontWeight="normal" onClick={handleToggle}>
                    {t('components.Menu.item.managementList')}
                  </Link>
                )}
                {/* MOC段階のため一時非表示。表示再開時はコメントを解除。
                <Link as={RouterLink} to="/ceas-status-report" display="block" px="8px" py="4px" fontWeight="normal" onClick={handleToggle}>
                  {t('components.Menu.item.statusReport')}
                </Link>
                */}
              </AccordionPanel>
            </AccordionItem>
            {canShowMasterMaintenance && (
              <AccordionItem borderColor="#1976D2">
                <AccordionButton justifyContent='space-between' fontSize="18px" fontWeight="bold" textAlign="left">{t('components.Menu.section.masterMaintenance')}<AccordionIcon /></AccordionButton>
                <AccordionPanel py={2} pl="32px" textAlign="left">
                  <Link as={RouterLink} to="/equipment-category" display="block" px="8px" py="4px" fontWeight="normal" onClick={handleToggle}>
                    {t('components.Menu.item.equipmentCategory')}
                  </Link>
                  <Link as={RouterLink} to="/equipment-group" display="block" px="8px" py="4px" fontWeight="normal" onClick={handleToggle}>
                    {t('components.Menu.item.equipmentGroup')}
                  </Link>
                  <Link as={RouterLink} to="/equipment-supplier" display="block" px="8px" py="4px" fontWeight="normal" onClick={handleToggle}>
                    {t('components.Menu.item.equipmentSupplier')}
                  </Link>
                  <Link as={RouterLink} to="/interest-rate-master" display="block" px="8px" py="4px" fontWeight="normal" onClick={handleToggle}>
                    {t('components.Menu.item.interestRateMaster')}
                  </Link>
                  <Link as={RouterLink} to="/company-plant" display="block" px="8px" py="4px" fontWeight="normal" onClick={handleToggle}>
                    {t('components.Menu.item.companyPlant')}
                  </Link>
                  <Link as={RouterLink} to="/nationality" display="block" px="8px" py="4px" fontWeight="normal" onClick={handleToggle}>
                    {t('components.Menu.item.nationality')}
                  </Link>
                  <Link as={RouterLink} to="/notification" display="block" px="8px" py="4px" fontWeight="normal" onClick={handleToggle}>
                    {t('components.Menu.item.notification')}
                  </Link>
                  <Link as={RouterLink} to="/accounting-organization" display="block" px="8px" py="4px" fontWeight="normal" onClick={handleToggle}>
                    {t('components.Menu.item.accountingOrganization')}
                  </Link>
                  <Link as={RouterLink} to="/system-access-setting" display="block" px="8px" py="4px" fontWeight="normal" onClick={handleToggle}>
                    {t('components.Menu.item.systemAccessSetting')}
                  </Link>
                  <Link as={RouterLink} to="/approval-requester-master" display="block" px="8px" py="4px" fontWeight="normal" onClick={handleToggle}>
                    {t('components.Menu.item.approvalRequesterMaster')}
                  </Link>
                  <Link as={RouterLink} to="/ai-restricted-organization" display="block" px="8px" py="4px" fontWeight="normal" onClick={handleToggle}>
                    {t('components.Menu.item.aiRestrictedOrganization')}
                  </Link>
                  <Link as={RouterLink} to="/login-history" display="block" px="8px" py="4px" fontWeight="normal" onClick={handleToggle}>
                    {t('components.Menu.item.loginHistory')}
                  </Link>
                  <Link as={RouterLink} to="/inquire-setting" display="block" px="8px" py="4px" fontWeight="normal" onClick={handleToggle}>
                    {t('components.Menu.item.inquireSetting')}
                  </Link>
                  <Link as={RouterLink} to="/check-list-master" display="block" px="8px" py="4px" fontWeight="normal" onClick={handleToggle}>
                    {t('components.Menu.item.checkListMaster')}
                  </Link>
                </AccordionPanel>
              </AccordionItem>
            )}
            <AccordionItem borderColor="#1976D2">
              <AccordionButton justifyContent='space-between' fontSize="18px" fontWeight="bold" textAlign="left">{t('components.Menu.section.setting')}<AccordionIcon /></AccordionButton>
              <AccordionPanel py={2} pl="32px" textAlign="left">
                <Text
                  as="button"
                  display="block"
                  px="8px"
                  py="4px"
                  fontWeight="normal"
                  textAlign="left"
                  w="100%"
                  _hover={{ textDecoration: 'underline' }}
                  onClick={() => { setIsInquireOpen(true); handleToggle(); }}
                >
                  {t('components.Menu.item.inquire')}
                </Text>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>

          {/* サインアウトボタン */}
          <Text
            as="button"
            onClick={handleClickSignOut}
            cursor="pointer"
            color="white"
            display="block"
            bgColor="#1565C0"
            borderRadius="8px"
            py="4px"
            _hover={{ cursor: "pointer" }}
            mb={2}
            mt="auto"
            w="100%"
            textAlign="center"
          >
            {t('common.button.signOut')}
          </Text>

          {/* バージョン表示 */}
          <Text
            textAlign="right"
            mt={8}
          >
            {t('components.Menu.version')}：{CEAS_APP_VERSION}
          </Text>
        </Box>
      )}
      <InquireModal isOpen={isInquireOpen} onClose={() => setIsInquireOpen(false)} />
    </>
  );
};

export default Menu;
